/* eslint-disable comma-dangle */
import { CHORD_DATA, INTERVAL_SIZE, DEGREE_NAMES, PITCH_INDEX } from '@/utils/musical_constants'
import {
  getPitchNumber,
  getPitchName,
  addIntervalToNoteName,
  getAccidentalNumber,
  getInterval,
  getKeyName,
  getPitchOctaveNumber
} from '@/utils/music_utils'

function cmpNumber (a, b) {
  return a - b
}

function cmpChord (c1, c2) {
  return !!c1.bass - !!c2.bass
}

// This maps a string of sorted tone sequence to a chord type
export let toChordType = {}

// This maps a chord type to an interval string.
export let intervalMap = {}
let initialized = false

function createMaps () {
  toChordType = {}
  intervalMap = {}
  for (const intervals in CHORD_DATA) {
    const arr = intervals.split(',').map(interval => INTERVAL_SIZE[interval]).sort(cmpNumber)
    toChordType[arr.toString()] = CHORD_DATA[intervals]
    if (intervalMap[CHORD_DATA[intervals]]) {
      console.error('Duplicated key!', CHORD_DATA[intervals], intervals, intervalMap[CHORD_DATA[intervals]])
    }
    intervalMap[CHORD_DATA[intervals]] = intervals
  }
  initialized = true
  console.log('Chord maps created.')
}

function toToneArray (seq) {
  let notes = Array.from(seq)
  notes = notes.map(x => x % 12)
  notes = new Set(notes)
  notes = Array.from(notes).sort(cmpNumber)
  return notes
}

// Lowest pitch should be 0
// function lookupChord (set) {
//   const notesStr = toToneArray(set).toString()
//   if (toChordType[notesStr]) {
//     return toChordType[notesStr]
//   } else {
//     return null
//   }
// }

export function lookupChords (seq) {
  if (!initialized) {
    createMaps()
  }
  const notes = toToneArray(seq) // notes should be in 0..11
  if (notes.length === 0) {
    return []
  }
  const results = []
  const bass = Math.min(...seq) % 12
  for (const root of notes) {
    const reArranged = notes.map(x => (x + 12 - root) % 12).sort(cmpNumber)
    const type = toChordType[reArranged.toString()]
    if (typeof (type) !== 'undefined') {
      let chordString = DEGREE_NAMES[1][root] + type
      if (bass !== root) {
        chordString += '/' + DEGREE_NAMES[1][bass]
      }
      results.push(Chord({
        root: DEGREE_NAMES[1][root],
        type: type,
        bass: bass !== root ? DEGREE_NAMES[1][bass] : null,
        chordString: chordString,
        voicing: Array.from(seq).sort(cmpNumber),
        intervals: intervalMap[type]
      }))
      // TODO: complete other components
    }
    results.sort(cmpChord)
  }
  return results
}

function extractRegex (regex, string) {
  if (regex.test(string)) {
    return [string.match(regex)[0], string.replaceAll(regex, '')]
  }
  return [null, string]
}

// TODO: verify this function
export function parseChordString (string) {
  let root = null
  let bass = null
  let type = null
  let extension = null
  let omit = null
  // let description = null
  let rest = null
  const omitRegex = /\s*no\s*5\s*/g;
  [omit, rest] = extractRegex(omitRegex, string)

  const bassRegex = /\s*\/[A-G](b|#|x|bb)?\s*/ig;
  [bass, rest] = extractRegex(bassRegex, rest)
  if (bass) {
    bass = bass.replaceAll('/', '')
  }
  const rootRegex = /[A-G](b|#|x|bb)?/ig;
  [root, rest] = extractRegex(rootRegex, rest)
  type = rest

  const extensionRegex = /\(?([b#]?(5|9|11|13),? ?)+\)?/g;
  // eslint-disable-next-line comma-spacing
  [extension,] = extractRegex(extensionRegex, rest)

  return Chord({
    root: root,
    type: type,
    bass: bass,
    extension: extension,
    omit: omit,
    chordString: string
  })
}

export function Chord ({
  root = null,
  type = null,
  bass = null,
  extension = null,
  omit = null,
  chordString = null,
  voicing = null,
  intervals = null
} = {}) {
  return {
    root: root,
    type: type,
    bass: bass,
    extension: extension,
    omit: omit,
    chordString: chordString,
    voicing: voicing,
    intervals: intervals ? intervals.split(',') : null,
    toString: function () {
      let result = ''
      result += root || ''
      result += type || ''
      result += extension || ''
      result += omit || ''
      result += bass ? `/${bass}` : ''
      return result
    },
    empty: function () {
      return !root
    },
    noteNames: function () {
      if (!intervals || !voicing.length) {
        return []
      } else {
        return voicing.map(pitch => {
          const p1 = getPitchNumber(root)
          const p2 = pitch
          const intervalSize = getInterval(p1, p2)
          // console.log('size', intervalSize)
          const interval = this.intervals.find(i => INTERVAL_SIZE[i] === intervalSize)
          // console.log('interval', interval)
          const letter = addIntervalToNoteName(root, interval)
          // console.log(root, interval, letter)
          return getPitchName(pitch, {
            specifiedLetter: letter[0],
            withOctave: true
          })
        })
      }
    }
  }
}

function randomChoice (arr) {
  return arr[Math.floor(Math.random() * arr.length)]
}

export function chordEqualRootType (c1, c2) {
  return PITCH_INDEX[c1.root] === PITCH_INDEX[c2.root] &&
    c1.type === c2.type
}

export function chordEqualVoicing (c1, c2) {
  return !!c1.voicing && !!c2.voicing &&
    c1.voicing.toString() === c2.voicing.toString()
}

export function RandomChord () {
  const root = randomChoice('A B C D E F G'.split(' ')) + (Math.random() > 0.5 ? randomChoice(['b', '#']) : '')
  const type = randomChoice(['', 'm7', '7', 'Maj7', '13', 'dim7', '7sus4'])
  return Chord({
    root: root,
    type: type,
    chordString: root + type
  })
}

export function EmptyChord () {
  return Chord()
}

export function chordTypeToToneArray (type) {
  const intervalString = intervalMap[type]
  if (intervalString !== undefined) {
    return intervalString.split(',')
      .map(interval => INTERVAL_SIZE[interval] % 12).sort()
  } else {
    console.error('Unrecognized type:', type)
  }
}

export function encodeToneArrayToInt (arr) {
  let tones = 0
  for (const pitch of arr) {
    const p = pitch % 12
    tones |= 1 << p
  }
  return tones
}

export function rotateToneArrayInteger (tones, numSteps) {
  numSteps = numSteps || 0
  numSteps %= 12
  for (let i = 0; i < numSteps; i++) {
    const LSB = tones & 1
    tones >>>= 1
    tones |= LSB << 11
  }
  return tones
}

export function matchToneArray (pattern, src) {
  const results = []
  for (let i = 0; i < 12; i++) {
    const srcRotated = rotateToneArrayInteger(src, i)
    // eslint-disable-next-line no-self-compare
    if ((srcRotated & pattern) === pattern) {
      results.push(i)
      // results.push({
      //   position: i,
      //   pattern: pattern.toString(2),
      //   srcRotated: srcRotated.toString(2),
      //   and: (srcRotated & pattern).toString(2)
      // })
    }
  }
  return results
}

export function testUtil () {
  console.log('getPitchNumber', getPitchNumber('A3'))
  console.log('getPitchNumber', getPitchNumber('B#3'))
  console.log('getPitchName', getPitchName(60))
  console.log('getPitchName', getPitchName(58, {
    specifiedLetter: 'C',
    withOctave: true
  }))
  console.log('addIntervalToNoteName', addIntervalToNoteName('A3', 'M3'))
  console.log('addIntervalToNoteName', addIntervalToNoteName('A3', 'U1'))
  console.log('addIntervalToNoteName', addIntervalToNoteName('Ab3', 'U1'))
  console.log('getAccidentalNumber', getAccidentalNumber('F'))
  console.log('getAccidentalNumber', getAccidentalNumber('#'))
  console.log('getInterval', getInterval(1, 5))
  console.log('getInterval', getInterval(5, 1))
  console.log('getKeyName', getKeyName(5))
  console.log('getKeyName', getKeyName(5, true))
  console.log('getPitchOctaveNumber', getPitchOctaveNumber(56))
  // This class
  console.log('parseChordString', parseChordString('C#7(#9)no5/Eb'))
  console.log('parseChordString', parseChordString('Dm13'))
  console.log('parseChordString', parseChordString('Bb13sus4'))
  console.log('lookupChord', lookupChords([60, 64, 67, 71])[0].noteNames())

  // get all chords
  console.log('preparing all chords...')
  const roots = []
  const chordTypes = []
  const notes = []
  let csv = 'root,type,0,1,2,3,4,5,6,7,8,9,10,11\n'
  for (let i = 0; i < 12; i++) {
    const root = DEGREE_NAMES[1][i]
    for (const intervalString of Object.keys(CHORD_DATA)) {
      const intervals = intervalString.split(',')
      const chordType = CHORD_DATA[intervals]
      const currentNotes = Array(12).fill(0)
      for (const interval of intervals) {
        // console.log(`intervals: ${JSON.stringify(intervals)} , root: ${root}`)
        const note = addIntervalToNoteName(root, interval)
        const pitch = getPitchNumber(note)
        currentNotes[pitch % 12] = 1
      }
      roots.push(i)
      chordTypes.push(chordType)
      notes.push(currentNotes)
      csv += `${i},"${chordType}",${currentNotes}\n`
    }
  }
  const dict = {
    roots: roots,
    chordTypes: chordTypes,
    notes: notes
  }
  console.log(JSON.stringify(dict))
  console.log(csv)
}
